import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { Arrow, TextLines } from "@/components/tool-kit/diagrams/svg-parts";
import { textColorOn } from "@/lib/tool-kit/charts/color";
import { LINE_HEIGHT } from "@/lib/tool-kit/charts/layout";
import {
  CATEGORY_FONT,
  type FishLayout,
  HEAD_FONT,
  type PlacedColumn,
} from "@/lib/tool-kit/diagrams-more/fishbone";
import { titledFrame } from "@/lib/tool-kit/diagrams-more/frame";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A fishbone diagram from `layoutFishbone`: the effect in a dark box at the head, a spine
 * running into it, and a coloured bone for each category with its causes as short twigs.
 * Sub-causes are set smaller and indented under their cause.
 */

function Column({ column, color }: { column: PlacedColumn; color: string }) {
  const [x1, y1, x2, y2] = column.bone;
  const lineHeight = CATEGORY_FONT * LINE_HEIGHT;
  return (
    <g>
      <line
        stroke={color}
        strokeLinecap="round"
        strokeWidth={2.5}
        x1={px(x1)}
        x2={px(x2)}
        y1={px(y1)}
        y2={px(y2)}
      />
      {column.rows.map((row) => (
        <g key={`row-${row.cause.line}`}>
          <line
            stroke={color}
            strokeWidth={row.cause.level === 0 ? 1.5 : 1}
            x1={px(row.twig.x1)}
            x2={px(row.twig.x2)}
            y1={px(row.twig.y)}
            y2={px(row.twig.y)}
          />
          <TextLines
            anchor="start"
            fill={CHART_INK.text}
            fontSize={row.fontSize}
            lineHeight={row.fontSize * LINE_HEIGHT}
            lines={row.lines}
            top={row.textY}
            x={row.textX}
          />
        </g>
      ))}
      <rect
        fill={color}
        height={px(column.box.height)}
        rx={6}
        stroke={color}
        strokeWidth={1.5}
        width={px(column.box.width)}
        x={px(column.box.x)}
        y={px(column.box.y)}
      />
      <TextLines
        bold
        fill={textColorOn(color)}
        fontSize={CATEGORY_FONT}
        lineHeight={lineHeight}
        lines={column.lines}
        top={
          column.box.y +
          (column.box.height - column.lines.length * lineHeight) / 2
        }
        x={column.box.x + column.box.width / 2}
      />
    </g>
  );
}

export function FishboneDiagram({
  id,
  layout,
  colors,
  title,
  label,
  description,
}: {
  id: string;
  layout: FishLayout;
  /** One colour per category, in order. */
  colors: readonly string[];
  title: string;
  label: string;
  description: string;
}) {
  const frame = titledFrame(layout.width, layout.height, title);
  const head = layout.head;
  const headLineHeight = HEAD_FONT * LINE_HEIGHT;
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitle title={frame.title} />
      <g transform={`translate(${px(frame.dx)} ${px(frame.dy)})`}>
        <Arrow
          color={CHART_INK.text}
          head={9}
          points={[
            [layout.spineStart, layout.spineY],
            [head.x, layout.spineY],
          ]}
          width={3}
        />
        {layout.columns.map((column) => (
          <Column
            color={colors[column.index] ?? CHART_INK.text}
            column={column}
            key={`column-${column.index}`}
          />
        ))}
        <rect
          fill={CHART_INK.text}
          height={px(head.height)}
          rx={8}
          width={px(head.width)}
          x={px(head.x)}
          y={px(head.y)}
        />
        <TextLines
          bold
          fill="#ffffff"
          fontSize={HEAD_FONT}
          lineHeight={headLineHeight}
          lines={layout.headLines}
          top={
            head.y +
            (head.height - layout.headLines.length * headLineHeight) / 2
          }
          x={head.x + head.width / 2}
        />
      </g>
    </ChartSvg>
  );
}
